import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ReactComponent as Edit } from '../../../../assets/common-assets/edit.svg';
import { lightPalette } from '../../../../theme';
import { classes, Root } from './utils';
import ChangePassword from '../../common-components/common-forms/change-password/ChangePassword';
import EditDetails from '../../common-components/common-forms/edit-details/EditDetails';
import { ACCOUNT_INFO } from '../../../../constants/staticContent';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import DonutChart from './DonutChart';
import { changeStatusTfa } from '../../../../services/accountSetting';
import ROLES from '../../../../constants/roles';
import CustomTooltip from '../../utils/CustomTooltip';
import Toast from '../../utils/Toast';
import { getPricingPlanDetails } from '../../../../services/userApi';

const MyAccount = ({ subscriptionDetails }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editDetailOpen, setEditDetailOpen] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [tfaStatus, setTfaStatus] = useState(false);
  const [totalCredit, setTotalCredit] = useState('');
  const [remainingCredit, setRemainingCredit] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [activePlanDetails, setActivePlanDetails] = useState();
  const [activeSubscription, setActiveSubscription] = useState();

  const userDetail = useSelector((state) => state.user.user);
  const nameMerge = `${userDetail?.first_name} ${userDetail?.last_name}`;
  const accountName = userDetail ? nameMerge : 'Name';
  const userAvatar = userDetail ? userDetail?.avatar : '';
  const role = getLocalStorageItem('role');

  // counting for plan date and expiry plan time with date
  // const paymentDate = userDetail?.organization?.payment[0]?.payment_date?.split('T')[0];
  // const paymentTime = userDetail?.organization?.payment[0]?.
  // payment_date?.split('T')[1]?.split('.')[0];

  useEffect(() => {
    const getActiveSubscription = subscriptionDetails?.subscriptions?.filter(
      (subscription) => subscription.status === 'active',
    );
    getActiveSubscription?.map((activeSubData) =>
      setActiveSubscription(activeSubData),
    );
  }, [subscriptionDetails.subscriptions]);

  const getActivePricingPlan = async () => {
    try {
      const response = await getPricingPlanDetails(activeSubscription?.plan_id);
      if (response?.data) {
        setActivePlanDetails(response?.data);
      }
    } catch (err) {
      return err;
    }
  };

  useEffect(() => {
    getActivePricingPlan();
  }, [activeSubscription?.plan_id]);
  const planStartDate =
    activeSubscription &&
    moment.utc(activeSubscription?.updated_at).local().format('DD-MM-YYYY');
  const planEndDate =
    activeSubscription &&
    activePlanDetails &&
    moment(activeSubscription?.updated_at)
      .add(activePlanDetails?.expiry, 'days')
      .local()
      .format('DD-MM-YYYY');
  const formatePlanExpiry =
    activeSubscription &&
    activePlanDetails &&
    moment(activeSubscription?.updated_at)
      .add(activePlanDetails?.expiry, 'days')
      .local()
      .format('MMMM D, YYYY');
  const handleAuthChecked = async (event) => {
    setTfaStatus(event.target.checked);
    const response = await changeStatusTfa(event.target.checked);
    if (response) {
      if (event.target.checked) {
        Toast({
          message: ACCOUNT_INFO.MY_ACCOUNT.TFA_STATUS.enabled,
          variant: 'success',
        });
      } else {
        Toast({
          message: ACCOUNT_INFO.MY_ACCOUNT.TFA_STATUS.disabled,
          variant: 'success',
        });
      }
    }
  };

  const openChangePassword = () => {
    setOpen(true);
  };
  const handleClick = () => {
    Toast({
      message: 'Our sales team will contact you soon',
      variant: 'warning',
    });
  };

  const openEditInfo = () => {
    setEditDetailOpen(true);
  };

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      setShowTooltip(windowWidth >= 600 && windowWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    if (userDetail && Object.keys(userDetail).length !== 0) {
      setIsLoading(false);
      setTfaStatus(userDetail?.is_tfa);
      const remainingCreditAvailable =
        userDetail?.organization?.remaining_credits || 0;
      const totalCreditAvailable = userDetail?.organization?.total_credits || 0;
      setRemainingCredit(remainingCreditAvailable);
      setTotalCredit(totalCreditAvailable);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [userDetail]);

  const renderDurationComponent = (tooltip) => {
    if (tooltip) {
      return (
        <CustomTooltip title={`${planStartDate} to ${planEndDate}`}>
          <Typography
            variant="body1"
            color={lightPalette.shoeWax.darker}
            noWrap
          >
            {`${ACCOUNT_INFO.BILLING_INFO.DURATION} : ${
              planStartDate &&
              planEndDate &&
              `${planStartDate} to ${planEndDate}`
            }`}
          </Typography>
        </CustomTooltip>
      );
    }
    return (
      <Typography variant="body1" color={lightPalette.shoeWax.darker} noWrap>
        {`${ACCOUNT_INFO.BILLING_INFO.DURATION} : ${
          planStartDate && planEndDate
            ? `${planStartDate} to ${planEndDate}`
            : ''
        }`}
      </Typography>
    );
  };

  const gridTitleBoarderBottom = (tooltip) => {
    const titleContent = (
      <Typography variant="h5" className={classes.gridItemMainTitle} noWrap>
        {ACCOUNT_INFO?.BILLING_INFO?.CHANGE_PASSWORD}
      </Typography>
    );

    if (tooltip) {
      return (
        <CustomTooltip title={ACCOUNT_INFO?.BILLING_INFO?.CHANGE_PASSWORD}>
          {titleContent}
        </CustomTooltip>
      );
    }
    return titleContent;
  };

  const renderEmailComponent = (tooltip) => {
    const emailTypography = (
      <Typography
        variant="body1"
        color={lightPalette.shoeWax.darker}
        className={
          role !== ROLES.MEMBER ? classes.emailBox : classes.emailBoxMember
        }
      >
        {userDetail?.email}
      </Typography>
    );

    if (tooltip) {
      return (
        <CustomTooltip title={userDetail?.email}>
          {emailTypography}
        </CustomTooltip>
      );
    }
    return emailTypography;
  };

  const billingInfoFirstGridBoxes = (tooltip) => {
    const titleContent = (
      <Typography variant="h5" className={classes.gridItemMainTitle} noWrap>
        {ACCOUNT_INFO?.BILLING_INFO?.AUTH_TITLE}
      </Typography>
    );

    if (tooltip) {
      return (
        <CustomTooltip title={ACCOUNT_INFO?.BILLING_INFO?.AUTH_TITLE}>
          {titleContent}
        </CustomTooltip>
      );
    }
    return titleContent;
  };

  let tfaStatusText = '';
  if (tfaStatus) {
    tfaStatusText = 'Enabled';
  } else {
    tfaStatusText = 'Disabled';
  }

  return (
    <Root className={classes.root}>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Grid container>
            <Grid
              container
              className={classes.accountInfoBillingInfoSecondGrid}
            >
              <Grid
                item
                xs={12}
                md={5.78}
                className={classes.billingInfoSecondGridBoxes}
              >
                <Box className={classes.secondGridTitleBoarderBottom}>
                  <Typography
                    variant="h5"
                    className={classes.gridItemMainTitle}
                  >
                    {ACCOUNT_INFO?.BILLING_INFO?.BILLING_CONTACT}
                  </Typography>
                  <Box>
                    <Edit
                      className={classes.changePasswordButton}
                      onClick={openEditInfo}
                    />
                    {userDetail?.email && (
                      <Backdrop
                        className={classes.changePasswordBackdrop}
                        open={editDetailOpen}
                      >
                        <EditDetails
                          setEditDetailOpen={setEditDetailOpen}
                          isClosed={isClosed}
                          setIsClosed={setIsClosed}
                          editDetailOpen={editDetailOpen}
                          userDetail={userDetail}
                        />
                      </Backdrop>
                    )}
                  </Box>
                </Box>
                <Box className={classes.addressAndPlanDetailBox}>
                  <Box className={classes.userDetailBox}>
                    <Avatar className={classes.avatar} src={userAvatar} />
                    <Box>
                      <Typography
                        variant="h5"
                        color={lightPalette.darkPurple.main}
                        className={classes.userNameBox}
                      >
                        {accountName}
                      </Typography>
                      <Typography
                        variant="body1"
                        color={lightPalette.shoeWax.darker}
                        className={classes.organizationNameBox}
                      >
                        {userDetail?.organization?.name}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.AdminAddress}>
                    {/* <Typography
                      variant="body1"
                      color={lightPalette.shoeWax.darker}
                      >
                        {userDetail.address}
                      </Typography> */}
                    <Typography
                      variant="body1"
                      color={lightPalette.shoeWax.darker}
                    >
                      {userDetail?.email}
                    </Typography>
                    <Typography
                      variant="body1"
                      color={lightPalette.shoeWax.darker}
                    >
                      {userDetail?.phone_no}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={5.78}
                className={classes.billingInfoSecondGridBoxes}
              >
                <Box className={classes.secondGridTitleBoarderBottom}>
                  <Typography
                    variant="h5"
                    className={classes.gridItemMainTitle}
                  >
                    {
                      ACCOUNT_INFO?.BILLING_INFO
                        ?.CURRENT_PLAN_AND_PAYMENT_INFORMATION
                    }
                  </Typography>
                </Box>
                <Box className={classes.planDetailBox}>
                  <Box
                    className={`${classes.addressAndPlanDetailBox} ${classes.planDetailAndPaymentInfo}`}
                  >
                    <Box className={classes.paymentDetailBox}>
                      {renderDurationComponent(showTooltip)}
                    </Box>
                    <Typography
                      variant="body1"
                      color={lightPalette.darkPurple.darker}
                      className={classes.planExpiryDate}
                    >
                      Expires on: {formatePlanExpiry}
                    </Typography>
                    <Button
                      className={classes.planExpiryDate}
                      variant="text"
                      onClick={() => handleClick()}
                    >
                      Contact Sales Team
                    </Button>
                  </Box>
                  <Box>
                    <DonutChart />
                    <Typography
                      variant="subtitle2"
                      className={classes.remainingCreditsTypography}
                    >
                      {remainingCredit} of {totalCredit} credits remaining
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Grid container className={classes.accountInfoBillingInfoFirstGrid}>
              <Grid
                item
                xs={12}
                md={5.78}
                className={classes.billingInfoFirstGridBoxes}
              >
                <Box className={classes.GridTitleBoarderBottom}>
                  {gridTitleBoarderBottom(showTooltip)}

                  <Box>
                    <Edit
                      className={classes.changePasswordButton}
                      onClick={openChangePassword}
                    />
                    <Backdrop
                      className={classes.changePasswordBackdrop}
                      open={open}
                    >
                      <ChangePassword setOpen={setOpen} />
                    </Backdrop>
                  </Box>
                </Box>
                <Box className={classes.subscriptionDetailBox}>
                  {renderEmailComponent(showTooltip)}
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={5.78}
                className={classes.billingInfoFirstGridBoxes}
              >
                <Box className={classes.GridTitleBoarderBottom}>
                  {billingInfoFirstGridBoxes(showTooltip)}
                </Box>
                <Box className={classes.subscriptionDetailBox} gap={2}>
                  <Typography
                    variant="body1"
                    color={lightPalette.shoeWax.darker}
                  >
                    {tfaStatusText}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={tfaStatus}
                        onChange={handleAuthChecked}
                      />
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </Root>
  );
};

const subscriptionDetailsPropTypes = PropTypes.arrayOf(PropTypes.object);

MyAccount.defaultProps = {
  subscriptionDetails: [{}],
};

MyAccount.propTypes = {
  subscriptionDetails: subscriptionDetailsPropTypes,
};

export default MyAccount;
